import store from "@/store";
import {
  LOGIN_ROUTE,
  EMAIL_VERIFICATION_WALL_ROUTE,
  DASHBOARD_ROUTE,
  ERROR_ROUTE,
  SUBSCRIPTION_WALL_ROUTE,
} from "@/router/routes";
import {
  CLEAR_ERRORMSG,
  GET_ORGANIZATION_ACTIVE_SUBSCRIPTION,
  GET_TRIAL_STATUS,
} from "@/store/action-types";
import { resetLoaders } from "@/utils/loaders";
import { resolveSafeFrom } from "@/router/utils/safe-redirect";
import { RouteLocationNormalized } from "vue-router";
import { queryClient } from "@/plugins/query-client";
import PermissionService from "@/services/permission-service";

export const ifNotAuthDeny = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: Function
) => {
  try {
    store.dispatch(CLEAR_ERRORMSG);

    resetLoaders();

    if (store.getters.sessionToken == null || store.getters.account.data == null) {
      return next({ name: LOGIN_ROUTE });
    }

    // During impersonation the admin gets full access — skip email verification
    // and subscription walls so they can navigate the entire app freely.
    if (store.getters.isImpersonating) {
      if (to.name === SUBSCRIPTION_WALL_ROUTE) {
        return next({ name: DASHBOARD_ROUTE });
      }
      return next();
    }

    if (store.getters.account.data && !store.getters.account.data.isEmailVerified) {
      if (to.name !== EMAIL_VERIFICATION_WALL_ROUTE) {
        return next({ name: EMAIL_VERIFICATION_WALL_ROUTE });
      }
      // Skip billing checks — user hasn't verified email yet, no point calling billing APIs
      return next();
    }

    try {
      await Promise.all([
        store.dispatch(GET_ORGANIZATION_ACTIVE_SUBSCRIPTION, {
          organizationId: store.getters.account.data.organizationId,
        }),
        store.dispatch(GET_TRIAL_STATUS),
        queryClient.prefetchQuery({
          queryKey: ["role-permissions"],
          queryFn: async () => {
            const result = await PermissionService.getRolePermissions();
            if (result.data?.status) return result.data.data;
            throw new Error(result.data?.msg || "Failed to load role permissions");
          },
          staleTime: 5 * 60 * 1000,
        }),
      ]);
    } catch (error: any) {
      const errorStatus = error?.response?.status;
      if (store.getters.sessionToken == null || errorStatus === 401 || errorStatus === 403) {
        return next({ name: LOGIN_ROUTE });
      }

      // If we have no cached subscription data, we cannot verify access — redirect to error.
      // Otherwise (stale cache), fall through and use what we have; the interceptor already
      // showed a "Network error" toast.
      const cachedSubData = store.getters.organizationActiveSubscription?.data;
      if (cachedSubData === null || cachedSubData === undefined) {
        const safeFrom = resolveSafeFrom(from.fullPath);
        return next({
          name: ERROR_ROUTE,
          query: {
            ...(safeFrom ? { from: safeFrom } : {}),
            code: "subscription_unreachable",
          },
        });
      }
      // fall through to the subscription-check logic below
    }

    // Re-check auth after async billing calls — an expired server-side session returns 403
    // (swallowed by the action) but also clears the store via handleForbidden
    if (store.getters.sessionToken == null || store.getters.account.data == null) {
      return next({ name: LOGIN_ROUTE });
    }

    const hasActiveSubscription = store.getters.organizationActiveSubscription.data;
    const isFreeTrialActive = store.getters.isFreeTrialActive;
    const hasFreeTrialDaysLeft = store.getters.hasFreeTrialDaysLeft;
    const isTrialing = store.getters.isTrialing;
    const isInternalUser = store.getters.isInternalUser;

    if (isInternalUser || isFreeTrialActive || hasFreeTrialDaysLeft || isTrialing) {
      if (to.name === SUBSCRIPTION_WALL_ROUTE) {
        return next({ name: DASHBOARD_ROUTE });
      }
      return next();
    } else if (!hasActiveSubscription) {
      if (to.name !== SUBSCRIPTION_WALL_ROUTE) {
        return next({ name: SUBSCRIPTION_WALL_ROUTE });
      } else {
        return next();
      }
    } else {
      if (to.name === SUBSCRIPTION_WALL_ROUTE) {
        return next({ name: DASHBOARD_ROUTE });
      } else {
        return next();
      }
    }
  } catch (error) {
    return next({ name: ERROR_ROUTE, query: { code: "guard_failure" } });
  }
};
